import { memo, useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import { Text } from '@mercator-gcp/grout';
import { EmptyField } from 'components/elements//EmptyField';
import Tooltip from 'components/elements/Tooltip';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { CompanyItem } from 'types';
import { formatCompanyName, formatCompanyAlias } from 'utils/format';

const tooltipStyle = {
  width: '100%',
};

const tooltipCompanyListStyle = { rowGap: '10px' };
const tooltipCompanyLabelStyle = { lineHeight: '13px' };

const companyListStyle = {
  whiteSpace: 'nowrap',
  rowGap: 'padding_4',
  width: '100%',
};

const companyLabelStyle = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const MAX_COMPANIES_TO_SHOW = 2;

type CompanyCellProps = {
  companyListString?: string;
  latestCompaniesArray?: CompanyItem[];
};

const CompanyCell = ({ companyListString, latestCompaniesArray }: CompanyCellProps) => {
  // Determine if we have companies with profiles (UUIDs) or just aliases
  const hasCompaniesWithProfiles =
    latestCompaniesArray && latestCompaniesArray.length > 0;

  // If we have companies with profiles, use those; otherwise fall back to string aliases
  const companyList = useMemo(() => {
    if (hasCompaniesWithProfiles) {
      return latestCompaniesArray!.map((company, idx) => ({
        name: company.name,
        hasProfile: !!company.uuid,
        uuid: company.uuid || `alias-${idx}`,
      }));
    }
    if (companyListString) {
      return companyListString.split(',').map((name, idx) => ({
        name: name.trim(),
        hasProfile: false,
        uuid: `alias-${name.trim()}-${idx}`,
      }));
    }

    return [];
  }, [hasCompaniesWithProfiles, latestCompaniesArray, companyListString]);

  if (companyList.length === 0) {
    return <EmptyField />;
  }

  const numOverflowCompanies = useMemo(
    () => companyList.length - MAX_COMPANIES_TO_SHOW,
    [companyList.length],
  );

  return (
    <Tooltip
      key={`company-cell-tooltip-${
        companyListString || JSON.stringify(latestCompaniesArray)
      }`}
      sx={tooltipStyle}
      fullWidth
      tooltipText={
        <FlexColumn sx={tooltipCompanyListStyle}>
          {companyList.map((company) => (
            <Text
              key={`company-cell-tooltip-text-${company.uuid}`}
              sx={tooltipCompanyLabelStyle}
            >
              {company.hasProfile
                ? formatCompanyName(company.name)
                : formatCompanyAlias(company.name)}
            </Text>
          ))}
        </FlexColumn>
      }
    >
      <FlexColumn sx={companyListStyle}>
        {companyList.slice(0, MAX_COMPANIES_TO_SHOW).map((company) => (
          <Text.BodyBold
            key={`company-cell-text-bold-${company.uuid}`}
            sx={companyLabelStyle}
          >
            {company.hasProfile
              ? formatCompanyName(company.name)
              : formatCompanyAlias(company.name)}
          </Text.BodyBold>
        ))}
        {numOverflowCompanies > 0 && <Box>{`+${numOverflowCompanies} more`}</Box>}
      </FlexColumn>
    </Tooltip>
  );
};

export default memo(CompanyCell);
